import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Lock, Pencil, Plus, ShieldAlert, Wand2, UploadCloud, CheckCircle2, AlertCircle, XCircle, ClipboardCheck, ArrowDownCircle } from "lucide-react";

const STATUS_BADGE = {
  complete: { label: "Completo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  pending_inputs: { label: "Falta inputs", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  pending_formula: { label: "Falta fórmula", cls: "bg-yellow-50 text-yellow-700 border-yellow-200", Icon: AlertCircle },
  pending_confirmation: { label: "Por confirmar", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: ClipboardCheck },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending_formula;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${s.cls}`} data-testid={`kpi-catalog-status-${status}`}>
      <s.Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function OpsKpiCatalog() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [kpisByTab, setKpisByTab] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, catsRes] = await Promise.all([
        api.get("/ops/admin/kpis/catalog-status"),
        api.get("/ops/kpis/categories"),
      ]);
      setStatus(statusRes.data);
      setCategories(catsRes.data || []);
      if (!activeTab && catsRes.data?.length) setActiveTab(catsRes.data[0].code);
    } catch {
      setStatus(null);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeTab) return;
    api.get(`/ops/kpis/definitions?category_code=${activeTab}`).then((r) => setKpisByTab(r.data || [])).catch(() => setKpisByTab([]));
  }, [activeTab]);

  const noCatalog = !loading && (!status || status.totals.categories === 0);

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="kpi-catalog-title">Catálogo maestro de KPIs</h1>
          <p className="text-sm text-slate-500">Los indicadores oficiales de cada puesto, con fórmula y meta ya definidas.</p>
        </div>
        {status && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate("/ops/admin/kpis/wizard-configuracion")}
              disabled={status.totals.kpis_pending_formula === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-40"
              data-testid="kpi-catalog-wizard-btn"
            >
              <Wand2 className="w-4 h-4" /> Configurar pendientes ({status.totals.kpis_pending_formula})
            </button>
            <button
              onClick={() => navigate("/ops/admin/kpis/revisar-propuestas")}
              disabled={!status.totals.kpis_pending_confirmation}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40"
              data-testid="kpi-catalog-review-proposals-btn"
            >
              <ClipboardCheck className="w-4 h-4" /> Revisar propuestas ({status.totals.kpis_pending_confirmation})
            </button>
          </div>
        )}
      </div>

      {status?.frozen && (
        <div className="flex items-start gap-3 bg-slate-900 text-white rounded-xl px-4 py-3" data-testid="kpi-catalog-frozen-banner">
          <Lock className="w-5 h-5 mt-0.5 shrink-0 text-[#FFDF00]" />
          <p className="text-sm">
            <strong>Catálogo bloqueado por política de la empresa.</strong> Los KPIs son fijos y solo pueden editarse
            (fórmulas, inputs, responsables). Para agregar KPIs nuevos contacta a soporte / Olimpo.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="bg-white rounded-xl border border-slate-200 h-16 animate-pulse" />)}</div>
      ) : noCatalog ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="kpi-catalog-empty">
          <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">Todavía no se cargó el catálogo maestro.</p>
          <button
            onClick={() => navigate("/ops/admin/kpis/importar-maestro")}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 transition-colors"
            data-testid="kpi-catalog-go-import-btn"
          >
            <UploadCloud className="w-4 h-4" /> Ir a cargar el catálogo maestro
          </button>
        </div>
      ) : (
        <>
          {/* Tabs por categoría */}
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {categories.map((cat) => {
              const prog = status?.categories_progress?.find((p) => p.code === cat.code);
              return (
                <button
                  key={cat.code}
                  onClick={() => setActiveTab(cat.code)}
                  data-testid={`kpi-catalog-tab-${cat.code}`}
                  className={`shrink-0 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors border ${
                    activeTab === cat.code ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {cat.name}
                  {prog && prog.pending > 0 && (
                    <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === cat.code ? "bg-[#FFDF00] text-slate-900" : "bg-yellow-100 text-yellow-800"}`}>
                      {prog.pending}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tabla de KPIs de la categoría activa */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <div className="font-semibold text-slate-900">{categories.find((c) => c.code === activeTab)?.name}</div>
                <div className="text-xs text-slate-400">{categories.find((c) => c.code === activeTab)?.objective}</div>
              </div>
              <button
                disabled
                title="Catálogo bloqueado. Contacta a Olimpo para desbloquear."
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-400 text-xs font-semibold cursor-not-allowed"
                data-testid="kpi-catalog-create-disabled-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Crear KPI nuevo
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5">Nombre</th>
                    <th className="px-4 py-2.5">Meta</th>
                    <th className="px-4 py-2.5">Rango aceptable</th>
                    <th className="px-4 py-2.5">Frecuencia</th>
                    <th className="px-4 py-2.5">Ponderación</th>
                    <th className="px-4 py-2.5">Estado</th>
                    <th className="px-4 py-2.5 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {kpisByTab.map((k) => (
                    <tr key={k.id} className="border-t border-slate-50" data-testid={`kpi-catalog-row-${k.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">
                        <span className="inline-flex items-center gap-1.5">
                          {k.name}
                          {k.score_direction === "lower_is_better" && (
                            <ArrowDownCircle className="w-3.5 h-3.5 text-blue-500" title="Menor es mejor" data-testid={`kpi-catalog-lower-icon-${k.id}`} />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{k.target_display || (k.target_value ?? k.target ?? "—")}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {k.acceptable_min != null && k.acceptable_max != null ? `${k.acceptable_min} – ${k.acceptable_max}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{k.frequency || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{k.weight_pct != null ? `${k.weight_pct}%` : "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={k.config_status} /></td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/ops/admin/kpis/definitions/${k.id}/editar`)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-black"
                          data-testid={`kpi-catalog-edit-btn-${k.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {kpisByTab.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin KPIs en esta categoría.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
